import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentPersonaApi } from './api.js';
import type {
  AgentPersona,
  CreateAgentPersona,
  LaunchFromPersona,
  UpdateAgentPersona,
} from '../shared/types.js';
import { queryKeys } from '@reflex/ui/client/query-keys';
import { useOrgKey } from '@reflex/ui/client/context';
import type { Agent } from '@reflex/shared';

type OrgKey = ReturnType<typeof useOrgKey>;

/**
 * Org-namespaced React Query key for the persona list. Distinct cache
 * slots per org prevent the previous org's personas from flashing on
 * org switch (the same pattern used by `plugin-ai-gateway`,
 * `plugin-mcp`, and `plugin-flows`).
 */
export const agentPersonasKey = (org: OrgKey) => ['agent-personas', org] as const;

/**
 * Org-namespaced React Query key for a single persona, used by the detail
 * page. Distinct from the list key so a deep-linked detail fetch and the
 * list can coexist in the cache.
 */
export const agentPersonaKey = (org: OrgKey, id: string) => ['agent-persona', org, id] as const;

export function useAgentPersonas() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: agentPersonasKey(orgKey),
    queryFn: () => agentPersonaApi.listPersonas(),
  });
}

/**
 * Fetch a single persona by id for the detail page. Seeds its initial value
 * from the list cache (when present) so navigating from the listing renders
 * instantly, then refetches the canonical row in the background. Supports
 * deep links / refreshes where the list was never loaded.
 *
 * `options` lets a display-only caller (e.g. the agent header's "Inherited
 * from" chip) opt out of retry and refetch-on-focus: a stale / foreign /
 * uninstalled persona id 404s, and retrying or refetching it on every window
 * focus would only re-issue failing requests for a chip that renders nothing.
 */
export function useAgentPersona(
  id: string,
  options?: { retry?: boolean | number; refetchOnWindowFocus?: boolean },
) {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: agentPersonaKey(orgKey, id),
    queryFn: () => agentPersonaApi.getPersona(id),
    enabled: id.length > 0,
    retry: options?.retry,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    placeholderData: () => {
      const cached = qc.getQueryData<AgentPersona[]>(agentPersonasKey(orgKey));
      return Array.isArray(cached) ? cached.find((p) => p.id === id) : undefined;
    },
  });
}

export function useCreateAgentPersona() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (data: CreateAgentPersona) => agentPersonaApi.createPersona(data),
    onSuccess: (created) => {
      qc.setQueryData<AgentPersona[]>(agentPersonasKey(orgKey), (old) =>
        old ? [...old, created] : [created],
      );
    },
  });
}

export function useUpdateAgentPersona() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentPersona }) =>
      agentPersonaApi.updatePersona(id, data),
    onSuccess: (updated) => {
      qc.setQueryData<AgentPersona[]>(
        agentPersonasKey(orgKey),
        (old) => old?.map((p) => (p.id === updated.id ? updated : p)) ?? [],
      );
      // Keep the detail-page cache in sync so an edit made from the detail
      // page (or the list) is reflected without a refetch.
      qc.setQueryData<AgentPersona>(agentPersonaKey(orgKey, updated.id), updated);
    },
  });
}

export function useDeleteAgentPersona() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => agentPersonaApi.deletePersona(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<AgentPersona[]>(
        agentPersonasKey(orgKey),
        (old) => old?.filter((p) => p.id !== id) ?? [],
      );
      qc.removeQueries({ queryKey: agentPersonaKey(orgKey, id) });
    },
  });
}

export function useLaunchFromPersona() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ personaId, body }: { personaId: string; body: LaunchFromPersona }) =>
      agentPersonaApi.launchPersona(personaId, body),
    onSuccess: (agent) => {
      qc.setQueryData<Agent>([...queryKeys.agent(orgKey, agent.id)], agent);
      // Prefix-match invalidation hits both the unfiltered list and the
      // owner-scoped variant under the active org.
      void qc.invalidateQueries({ queryKey: ['agents', orgKey] });
    },
  });
}
